/**
 * Registro de iconos lucide para el catálogo de módulos.
 *
 * El backend guarda el nombre del icono (`modulos.icono_web`, en kebab-case) de
 * forma declarativa. Aquí se resuelve a un componente real:
 *
 *  - se aceptan alias (nombres antiguos y nuevos de lucide) para que el dato de
 *    la BD no dependa de la versión instalada;
 *  - cualquier valor desconocido cae en un icono por defecto, de modo que un
 *    dato mal cargado nunca rompe la interfaz.
 */

import {
  Award,
  ArrowLeftRight,
  Banknote,
  BookOpen,
  Bot,
  Box,
  Boxes,
  Briefcase,
  Calculator,
  CalendarCheck,
  ChartBar,
  ChartLine,
  ChartPie,
  CircleArrowDown,
  CircleArrowUp,
  CirclePlus,
  CircleQuestionMark,
  ClipboardList,
  Clock,
  Coins,
  CreditCard,
  Droplets,
  FileClock,
  FileText,
  FolderTree,
  Fuel,
  Gauge,
  Gift,
  HandCoins,
  Headset,
  IdCard,
  Landmark,
  Layers,
  List,
  ListChecks,
  Monitor,
  MonitorSmartphone,
  Package,
  PiggyBank,
  Receipt,
  Ruler,
  Satellite,
  Server,
  Settings,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Store,
  Tags,
  Target,
  Terminal,
  TrendingDown,
  TrendingUp,
  Truck,
  UserCog,
  UserRound,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

/** kebab-case (BD) → componente lucide. */
const ICONS: Record<string, LucideIcon> = {
  award: Award,
  'arrow-left-right': ArrowLeftRight,
  banknote: Banknote,
  'bar-chart': ChartBar,
  'chart-bar': ChartBar,
  'line-chart': ChartLine,
  'chart-line': ChartLine,
  'pie-chart': ChartPie,
  'chart-pie': ChartPie,
  'book-open': BookOpen,
  bot: Bot,
  box: Box,
  boxes: Boxes,
  briefcase: Briefcase,
  calculator: Calculator,
  'calendar-check': CalendarCheck,
  clock: Clock,
  coins: Coins,
  'credit-card': CreditCard,
  droplets: Droplets,
  'file-text': FileText,
  'folder-tree': FolderTree,
  fuel: Fuel,
  gauge: Gauge,
  gift: Gift,
  'hand-coins': HandCoins,
  history: FileClock,
  'id-card': IdCard,
  landmark: Landmark,
  list: List,
  'list-checks': ListChecks,
  monitor: Monitor,
  'monitor-smartphone': MonitorSmartphone,
  package: Package,
  'piggy-bank': PiggyBank,
  receipt: Receipt,
  ruler: Ruler,
  satellite: Satellite,
  server: Server,
  settings: Settings,
  'shield-check': ShieldCheck,
  'shopping-cart': ShoppingCart,
  sliders: SlidersHorizontal,
  'sliders-horizontal': SlidersHorizontal,
  smartphone: Smartphone,
  sparkles: Sparkles,
  store: Store,
  tags: Tags,
  target: Target,
  terminal: Terminal,
  'trending-down': TrendingDown,
  'trending-up': TrendingUp,
  truck: Truck,
  'user-cog': UserCog,
  'user-round': UserRound,
  users: Users,
  wrench: Wrench,
  'arrow-down-circle': CircleArrowDown,
  'circle-arrow-down': CircleArrowDown,
  'arrow-up-circle': CircleArrowUp,
  'circle-arrow-up': CircleArrowUp,
  'circle-plus': CirclePlus,
  'plus-circle': CirclePlus,
  'clipboard-list': ClipboardList,
  'circle-question-mark': CircleQuestionMark,
  'file-clock': FileClock,
  headset: Headset,
  database: Server,
};

/** Icono por defecto cuando el nombre no está registrado. */
export const DEFAULT_MODULE_ICON = Layers;

export function resolveModuleIcon(name: string | null | undefined): LucideIcon {
  if (!name) return DEFAULT_MODULE_ICON;

  return ICONS[name.trim().toLowerCase()] ?? DEFAULT_MODULE_ICON;
}

// ------------------------------------------------------------------
// Tono semántico → utilidades de Tailwind
// ------------------------------------------------------------------

export type ModuleTone = 'brand' | 'emerald' | 'amber' | 'rose' | 'ink';

const TONES_BY_COLOR: Record<string, ModuleTone> = {
  primary: 'brand',
  info: 'brand',
  success: 'emerald',
  warning: 'amber',
  danger: 'rose',
  secondary: 'ink',
  dark: 'ink',
};

export const TONE_CLASSES: Record<ModuleTone, { icon: string; ring: string; bar: string }> = {
  brand: { icon: 'bg-brand-50 text-brand-600', ring: 'ring-brand-100', bar: 'bg-brand-500' },
  emerald: { icon: 'bg-emerald-50 text-emerald-600', ring: 'ring-emerald-100', bar: 'bg-emerald-500' },
  amber: { icon: 'bg-amber-50 text-amber-600', ring: 'ring-amber-100', bar: 'bg-amber-500' },
  rose: { icon: 'bg-rose-50 text-rose-600', ring: 'ring-rose-100', bar: 'bg-rose-500' },
  ink: { icon: 'bg-ink-100 text-ink-600', ring: 'ring-ink-200', bar: 'bg-ink-500' },
};

export function resolveTone(color: string | null | undefined): ModuleTone {
  if (!color) return 'brand';
  return TONES_BY_COLOR[color.trim().toLowerCase()] ?? 'brand';
}
